/**
 * Format fini et fond perdu d'un document — calculs PURS, sans store ni DOM.
 *
 * Dans LabelForge, l'origine (0, 0) des calques est le coin du FORMAT FINI, et
 * le fond perdu (`canvas.bleed_mm`) déborde tout autour, en coordonnées
 * négatives : c'est ce que rend PrintView, qui décale chaque calque de `bleed`.
 *
 * Un PDF à fond perdu arrive autrement : sa page englobe le fond perdu, et sa
 * TrimBox, plus petite, marque le format fini. Le serveur renvoie les marges de
 * cette TrimBox (`dimensions.trim_mm`, cf. routes/pdf.py::_marges_trimbox_mm) ;
 * ce module en tire le format, le fond perdu et le décalage des calques.
 *
 * Jusqu'au 2 octobre 2026, on prenait la page entière pour format : le fond
 * perdu entrait dans le document, `bleed_mm` restait à 0, et l'export ne savait
 * plus l'ajouter ni placer ses traits de coupe (retour de Louise, 2026-10-01).
 */

const arrondi = (n) => Math.round(n * 1000) / 1000;

/**
 * Cadrage d'un import PDF : le format du document, son fond perdu, et de
 * combien décaler les calques extraits de la page.
 *
 * @param {{width_mm:number, height_mm:number, trim_mm?:{left,top,right,bottom,bleed}}} dimensions
 * @returns {{width_mm:number, height_mm:number, bleed_mm:number|null, dx:number, dy:number}}
 *          `bleed_mm` vaut null quand le PDF ne déclare pas de fond perdu : le
 *          document garde alors son réglage par défaut, comme avant.
 */
export function cadrageImport(dimensions) {
    const w = dimensions?.width_mm;
    const h = dimensions?.height_mm;
    const t = dimensions?.trim_mm;
    if (!t || !(t.bleed > 0)) return { width_mm: w, height_mm: h, bleed_mm: null, dx: 0, dy: 0 };
    return {
        width_mm: arrondi(w - t.left - t.right),
        height_mm: arrondi(h - t.top - t.bottom),
        bleed_mm: t.bleed,
        dx: t.left,
        dy: t.top,
    };
}

/**
 * Translate des calques de (-dx, -dy). Un calque sans géométrie propre (un
 * nœud de groupe) passe tel quel : il n'a ni `x` ni `y` à déplacer.
 *
 * Pas d'arrondi ici : il déplacerait un calque d'un demi-micron, et l'invariant
 * qui prouve ce module — la page exportée ne change pas d'un pixel — tomberait.
 */
export function decalerCalques(elements, dx, dy) {
    if (!dx && !dy) return elements;
    return elements.map((el) => ({
        ...el,
        ...(typeof el.x === 'number' ? { x: el.x - dx } : {}),
        ...(typeof el.y === 'number' ? { y: el.y - dy } : {}),
    }));
}

/**
 * Un modèle .lfgr enregistré sur la PAGE ENTIÈRE d'un PDF à fond perdu — le
 * cas de tous les modèles faits avant ce correctif — place ses calques dans le
 * repère de la page, pas dans celui du format fini. Appliqué à l'import du même
 * genre de PDF, il doit subir le même décalage que les calques du PDF, sinon il
 * tombe à côté de la largeur du fond perdu. On le reconnaît à son format : celui
 * de la page, à un demi-millimètre près (même tolérance que `formatDifferent`).
 */
export function modeleSurPageEntiere(canvasModele, dimensions) {
    if (!canvasModele?.width_mm || !canvasModele?.height_mm || !dimensions?.trim_mm) return false;
    return Math.abs(canvasModele.width_mm - dimensions.width_mm) <= 0.5
        && Math.abs(canvasModele.height_mm - dimensions.height_mm) <= 0.5;
}

/**
 * Réparation d'un document dont le format COMPREND déjà son fond perdu (un PDF
 * importé avant ce correctif, les gabarits de l'atelier) : le format se resserre
 * de `b` de chaque côté, le fond perdu devient `b`, et les calques reculent de
 * `b` pour rester exactement où ils étaient sur la page. Rien ne bouge à
 * l'export avec fond perdu ; tout ce qui change, c'est que le PDF sait enfin où
 * est le format fini.
 *
 * @returns {{canvas:{width_mm,height_mm,bleed_mm}, elements:Array}|null}
 *          null si `b` ne laisse pas de format (au moins 1 mm de chaque côté).
 */
export function sortirFondPerduDuFormat(canvas, elements, b) {
    const fond = Number(b);
    if (!(fond > 0)) return null;
    const width_mm = arrondi(canvas.width_mm - 2 * fond);
    const height_mm = arrondi(canvas.height_mm - 2 * fond);
    if (width_mm < 1 || height_mm < 1) return null;
    return {
        canvas: { width_mm, height_mm, bleed_mm: fond },
        elements: decalerCalques(elements, fond, fond),
    };
}
